import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import {
  Grid,
  Box,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  Button,
} from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import Header from "../../../Layouts/Components/Header";
import CustomButton from "../../shared/CustomButton";
import * as yup from "yup";
import BankLogo from "./BankLogo";
import Control from "./Control";
import AccountNumber from "./AccountNumber";
import StmpConfig from "./StmpConfig";
import CurrenciesSettings from "./CurrenciesSettings";
import useSnackBar from "../../Hooks/useSnackBar";
import usePrompt from "../../Hooks/usePrompt";
import SaveChangesModal from "../../shared/SaveChangesModal";
import ScreenLoader from "../../shared/ScreenLoader";
import {
  updateConfiguration,
  getConfiguration,
  getCurrencies,
  resetBankSettings,
  createConfiguration,
  toggleUatMode,
} from "../state/actions";
import CustomSwitch from "../../shared/CustomSwitch";
/* import CorrespondentBic from "./CorrespondentBic";
 */
const validationSchema = yup.object().shape({
  bankLogoUrl: yup
    .string()
    .required("This field is mandatory")
    .url("Invalid URL"),
  // bankName: yup.string().required("This field is mandatory"),
  // bankBic: yup.string().required("This field is mandatory"),
  ltLetter: yup
    .string()
    .matches(/^[a-zA-Z ]*$/, "Must be a character")
    .max(1, "Maximum 1 character")
    .required("This field is mandatory"),
  stationNumber: yup
    .number()
    .typeError("Must be a number")
    .integer()
    .required("This field is mandatory"),
  website: yup.string().url("Invalid URL").required("This field is mandatory"),
  email: yup
    .string()
    .email("Not a valid email address")
    .required("This field is mandatory"),
  /*  nationalId: yup.string().when("noOrIBAN", {
    is: (value) => value === "both" || value === "no",
    then: yup
      .string()
      .required("This field is mandatory")
      .matches(/^[0-9]+$/, "Must be only numbers")
      .min(14, "Must be exactly 14 digits")
      .max(14, "Must be exactly 14 digits"),
  }), */
  accountLength: yup.number().when("noOrIBAN", {
    is: (value) => value === "both" || value === "no",
    then: yup
      .number()
      .typeError("Must be a number")
      .required("This field is mandatory"),
  }),
  accountPositionInput: yup.string().when("noOrIBAN", {
    is: (value) => value === "both" || value === "no",
    then: yup
      .string()
      .matches(/^[0-9]+(,[0-9]+)$/, "Wrong format")
      .required("This field is mandatory"),
  }),
  IBANLength: yup.number().when("noOrIBAN", {
    is: (value) => value === "both" || value === "IBAN",
    then: yup
      .number()
      .typeError("Must be a number")
      .min(15, "IBAN Cannot be less than 15 charachters")
      .max(34, "IBAN Cannot ecxeed 34 charachters")
      .required("This field is mandatory"),
  }),
  BBANLength: yup.number().when("noOrIBAN", {
    is: (value) => value === "both" || value === "IBAN",
    then: yup
      .number()
      .min(13, "BBAN Cannot be less than 13 charachters")
      .max(32, "BBAN Cannot ecxeed 32 charachters")
      .typeError("Must be a number")
      .required("This field is mandatory"),
  }),
  accountPositionOutput: yup.string().when("noOrIBAN", {
    is: (value) => value === "both" || value === "no",
    then: yup
      .string()
      .matches(/^[0-9]+(,[0-9]+)$/, "Wrong format")
      .required("This field is mandatory"),
  }),
  branchPosition: yup.string().when("noOrIBAN", {
    is: (value) => value === "both" || value === "no",
    then: yup
      .string()
      .matches(/^[0-9]+(,[0-9]+)$/, "Wrong format")
      .required("This field is mandatory"),
  }),
  IBANBranchPosition: yup.string().when("noOrIBAN", {
    is: (value) => value === "both" || value === "IBAN",
    then: yup
      .string()
      .matches(/^[0-9]+(,[0-9]+)$/, "Wrong format")
      .required("This field is mandatory"),
  }),
  IBANAccountPosition: yup.string().when("noOrIBAN", {
    is: (value) => value === "both" || value === "IBAN",
    then: yup
      .string()
      .matches(/^[0-9]+(,[0-9]+)$/, "Wrong format")
      .required("This field is mandatory"),
  }),
  branchLength: yup.number().when("noOrIBAN", {
    is: (value) => value === "both" || value === "no",
    then: yup
      .number()
      .typeError("Must be a number")
      .required("This field is mandatory"),
  }),
  clientIdLength: yup.number().when("noOrIBAN", {
    is: (value) => value === "both" || value === "no",
    then: yup
      .number()
      .typeError("Must be a number")
      .required("This field is mandatory"),
  }),
  logSystem: yup
    .string()
    .oneOf(["USER_PC"], "This field is mandatory")
    .required("This field is mandatory"),
  controlMode: yup.string().oneOf(["NORMAL", "MAKER_CHECKER"]),
  currencies: yup
    .array()
    .of(yup.object())
    .min(1, "This field is mandatory")
    .required("This field is mandatory"),
  clearanceCode: yup.string().required("This field is mandatory"),
  smtpEmail: yup.string().required("This field is mandatory"),
  //smtpUsername: yup.string().required("This field is mandatory"),
  //smtpPassword: yup.string().required("This field is mandatory"),
  //smtpIp: yup.string().matches(/^([0-9a-zA-Z]+\.){3}[0-9a-zA-Z]+$/, "Invalid IP").required("This field is mandatory"),
  smtpIp: yup.string().required("This field is mandatory"),
  smtpPort: yup.string().required("This field is mandatory"),
  smtpAutoTLS: yup.boolean(),
});

export const BankSettings = () => {
  const {
    processing,
    loadingConfiguration,
    configuration,
    currencies,
    uatMode,
    sendingTestEmail,
  } = useSelector((state) => state.bankSettings);
  const dispatch = useDispatch();
  const { success, error, snackbar } = useSnackBar();
  const { keycloak } = useKeycloak();

  const [createNew, setCreateNew] = useState(false);
  const [touched, setTouched] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);

  const Navigate = useNavigate();
  const loadConfiguration = useCallback(
    () => dispatch(getConfiguration(error)),
    [dispatch, error]
  );
  const loadCurrencies = useCallback(
    () => dispatch(getCurrencies(error)),
    [dispatch, error]
  );
  const reset = useCallback(() => dispatch(resetBankSettings()), [dispatch]);
  const navigate = useCallback((location) => Navigate(location), [Navigate]);

  const formik = useFormik({
    initialValues: {
      bankLogoUrl: configuration?.bankLogoUrl || "",
      bankName: configuration?.bankName || keycloak.tokenParsed.bankName || "",
      bankBic: configuration?.bankBic || keycloak.tokenParsed.BIC || "",
      ltLetter: configuration?.ltLetter || "",
      stationNumber: configuration?.stationNumber || 0,
      website: configuration?.website || "",
      email: configuration?.email || "",
      //end_of_day:configuration?.end_of_day,
      timeOption: configuration?.endOfDay?.slice(0, -2) || "",
      dayOption: configuration?.endOfDay?.slice(-2) || "",
      nationalId: configuration?.nationalId || "",
      accountLength: configuration?.accountLength || 0,
      accountPositionInput: configuration?.accountPositionInput || "",
      IBANLength: configuration?.IBANLength || 0,
      BBANLength: configuration?.BBANLength || 0,
      accountPositionOutput: configuration?.accountPositionOutput || "",
      IBANBranchPosition: configuration?.IBANBranchPosition || "",
      IBANAccountPosition: configuration?.IBANAccountPosition || "",
      branchLength: configuration?.branchLength || 0,
      branchPosition: configuration?.branchPosition || "",
      clientIdLength: configuration?.clientIdLength || 0,
      controlMode: configuration?.controlMode || "NORMAL",
      logSystem: configuration?.logSystem || "SELECT LOG SYSTEM",
      currencies: configuration?.currencies || [],
      clearanceCode: configuration?.clearanceCode || "",
      smtpEmail: configuration?.smtpEmail || "",
      smtpUsername: configuration?.smtpUsername || "",
      smtpPassword: configuration?.smtpPassword || "",
      smtpIp: configuration?.smtpIp || "",
      smtpPort: configuration?.smtpPort || "",
      smtpAutoTLS: configuration?.smtpAutoTLS || false,
      noOrIBAN: "both",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,

    onSubmit: (values) => {
      const data = {
        ...values,
        endOfDay: `${values.timeOption}${values.dayOption}`,
      };
      delete data.reciepentfortesting;
      //temporary until BE addition
      //remove un-needed props in the body request
      delete data.timeOption;
      delete data.dayOption;
      delete data.bankBic;
      delete data.bankName;
      delete data.noOrIBAN;

      if (!createNew)
        dispatch(updateConfiguration(data, success, error)).then(() => {
          setTouched(false);
          setIsConfirmationModalOpen(false);
        });
      else
        dispatch(createConfiguration(data, success, error)).then(() => {
          setTouched(false);
          setIsConfirmationModalOpen(false);
          setCreateNew(false);
        });
    },
  });

  const handleBlockedNavigation = useCallback(
    (nextLocation) => {
      if (nextLocation.pathname === "/admin-tool/bank-settings") return true;
      if (touched) {
        setPendingLocation(nextLocation.pathname);
        setIsConfirmationModalOpen(true);
        return false;
      }
      return true;
    },
    [touched]
  );

  //close navigation blocker handler
  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setPendingLocation(null);
  };

  //discard changes action handler
  const handleDiscardChanges = () => {
    setIsConfirmationModalOpen(false);
    setTouched(false);
  };

  //navigate to pendingLocation effect
  useEffect(() => {
    if (pendingLocation && !touched) navigate(pendingLocation);
  }, [navigate, pendingLocation, touched]);

  useEffect(() => {
    loadConfiguration();
    loadCurrencies();
    return () => {
      reset();
    };
  }, [loadConfiguration, loadCurrencies, reset]);

  //Navigation blocker
  usePrompt(handleBlockedNavigation, touched);

  // switch
  const [open, setOpen] = useState(false);
  const handleChange = () => {
    if (uatMode === false) {
      setOpen(true);
    } else {
      dispatch(toggleUatMode()).then(() => {
        dispatch(getConfiguration(error));
      });
    }
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleAgree = () => {
    dispatch(toggleUatMode()).then(() => {
      dispatch(getConfiguration(error));
      setOpen(false);
    });
  };

  const handleDeactivateUAT = () => {
    dispatch(toggleUatMode()).then(() => {
      dispatch(getConfiguration());
    });
  };
  return (
    <>
      {snackbar}
      <SaveChangesModal
        open={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        processing={processing}
        discardChanges={handleDiscardChanges}
        saveChanges={formik.handleSubmit}
      />
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle
          sx={{
            fontSize: "20px",
            fontWeight: "bold",
            letterSpacing: "-0.67px",
            lineHeight: " 24px",
            paddingInline: "24px",
          }}
          id="alert-dialog-title"
        >
          {"Enable UAT Mode"}
        </DialogTitle>
        <DialogContent
          sx={{
            width: "450px",
            paddingInline: "24px",
          }}
        >
          <DialogContentText id="alert-dialog-description">
            Would you like to change status from production mode to UAT mode? If
            yes, please note that bla bla bla
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              textTransform: "capitalize",
              border: " 1px solid #DBDEE6",
              borderRadius: "4px",
              color: "black",
              fontWeight: "bold",
              background: "linear-gradient(180deg, #FFFFFF 0%, #F3F5F7 100%)",
            }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            sx={{
              textTransform: "capitalize",
              backgroundColor: "#1492AF",
              color: "white",
              "&:hover": {
                backgroundColor: "#1492AF",
              },
            }}
            onClick={handleAgree}
            autoFocus
          >
            Yes, Enable UAT Mode
          </Button>
        </DialogActions>
      </Dialog>
      <Header headLine="Bank Settings">
        <CustomSwitch checked={uatMode} onChange={handleChange} />

        <span
          style={{
            color: "black",
            marginInline: "10px",
            fontWeight: "bold",
            fontSize: "14px",
            letterSpacing: "-0.5px",
          }}
        >
          UAT Mode
        </span>
        <CustomButton
          text="Update Settings"
          processing={processing}
          action={formik.handleSubmit}
          disabled={!touched}
        />
      </Header>
      {loadingConfiguration ? (
        <ScreenLoader />
      ) : !configuration && !createNew ? (
        <Box p={4}>
          <CustomButton
            processing={false}
            text="Create configuration"
            type="update"
            disabled={false}
            action={() => setCreateNew(true)}
          />
        </Box>
      ) : (
        <>
          {uatMode ? (
            <div
              style={{
                width: "260px",
                fontSize: "14px",
                backgroundColor: "#FFF5CA",
                paddingLeft: "10px",
                color: "#403200",
                marginLeft: "30px",
              }}
            >
              <p style={{ paddingBlock: "7px", marginBottom: "0px" }}>
                UAT Mode is now active.
                <span
                  onClick={handleDeactivateUAT}
                  style={{
                    fontWeight: "bold",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Deactivate
                </span>
              </p>
            </div>
          ) : (
            <div></div>
          )}
          <Grid
            container
            rowSpacing={5}
            justifyContent="flex-start"
            padding="30px"
          >
            <Grid padding="0px" item xs={12}>
              <BankLogo formik={formik} setTouched={setTouched} />
            </Grid>
            <Grid item xs={12}>
              <AccountNumber
                formik={formik}
                setTouched={setTouched}
                lookup={configuration}
              ></AccountNumber>
            </Grid>
            <Grid item xs={12}>
              <Control formik={formik} setTouched={setTouched} />
            </Grid>
          {/*   <Grid item xs={12}>
            {currencies && (
                <CorrespondentBic
                  formik={formik}
                  setTouched={setTouched}
                  currencies={currencies}
                />
              )}  
            </Grid> */}
            <Grid item xs={12}>
              {currencies && (
                <CurrenciesSettings
                  formik={formik}
                  setTouched={setTouched}
                  currencies={currencies}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <StmpConfig
                formik={formik}
                setTouched={setTouched}
                success={success}
                error={error}
                sendingTestEmail={sendingTestEmail}
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};
